import React, {useEffect, useRef} from "react";
import {injectIntl} from "react-intl";
import PropTypes from "prop-types";
import {useFormikContext} from "formik";

// core components
import FormikInput from "components/input/formikInput";

// utils
import {getReturnLineItemOutcomes, transitionCarriesReturnOutcome} from "utils/returnLineItemOutcomes";

/**
 * What the reason records on the return line item, and pushes to the eshop.
 *
 * Only shown for the transitions that take an article back out of a return: elsewhere there is no
 * return line item to write on. Left empty the return line item is not touched at all, which is the
 * safe default — but it also means the refusal stays invisible on the eshop, so a reason meant to
 * refuse a return should always carry one.
 */
function ReturnOutcomeField(props) {
    const {intl} = props;

    const formikContext = useFormikContext();
    const transition = formikContext.values.transition;

    function usePreviousValue(value) {
        const ref = useRef();

        useEffect(() => {
            ref.current = value;
        });

        return ref.current;
    }

    const previousTransition = usePreviousValue(transition);

    // Moving to a transition that has no return line item behind it leaves a stale outcome that
    // would never be applied; clear it rather than persist a value the reason cannot honour.
    useEffect(() => {
        if (previousTransition !== undefined && !transitionCarriesReturnOutcome(transition)) {
            formikContext.setFieldValue('returnLineItemTransition', '');
        }
    }, [transition]);

    if (!transitionCarriesReturnOutcome(transition)) {
        return null;
    }

    const outcomeOptions = () => ([
        <option value={''} key={'none'}>
            {intl.formatMessage({id: 'cancellation_reason.form.field.returnLineItemTransition.none'})}
        </option>,
        ...getReturnLineItemOutcomes().map((outcome, key) => (
            <option value={outcome} key={key}>
                {intl.formatMessage({id: 'cancellation_reason.form.field.returnLineItemTransition.' + outcome})}
            </option>
        )),
    ]);

    return (
        <FormikInput
            labelTranslationId={'cancellation_reason.form.label.returnLineItemTransition'}
            inputProps={
                {
                    name: 'returnLineItemTransition',
                    as: 'select',
                    children: outcomeOptions(),
                }
            }
        />
    );
}

ReturnOutcomeField.propTypes = {
    transition: PropTypes.string
};

export default injectIntl(ReturnOutcomeField);
